import React, { useEffect, useState } from 'react';

import DragNDropUploader from '../utilities/drag-n-drop-uploader/drag-n-drop-uploader';

import HttpService from '../../services/http.service';

import QuickForm, { quickFormPartialInputHandler, QuickFormSchemaMetaType } from '../utilities/quick-form/quick-form';

import Spinner from '../utilities/spinner/spinner';

import Media, { getMediaType } from '../utilities/media/media';
import config from '../../config/config';
import ColumnSnapper from '../utilities/column-snapper/column-snapper';
import { ModalContext } from '../modal/modal';
import { StorageContext } from '../storage/storage-context'

import { Theme } from '../../models/models'

type MetaData = { 
  googleId?: string,
  mimeType?: string, 
  width?: number, 
  height?: number, 
  durationMillis?: string 
}

type File = {
  googleId?: string,
  size: number;
  timestamp: number;
  metaData?: MetaData;
};

type Directory = {
  googleId?: string,
  size: number | undefined;
  timestamp: number;
  files: { [filename: string]: File };
  subDirectories: { [directoryname: string]: Directory };
};

type FileBrowserProps = {
  onPick?: (filepath: string, metaData?: MetaData) => any;
  acceptedMediaExtensions?: { [key: string]: string[] }
  sources?: { [key: string]: {
    readDirectoryRoute: string;
    createDirectoryRoute?: string;
    deleteMediaRoute?: string;
    uploadMediaRoute?: string;
  } }
};

const defaultAcceptedMediaExtensions = {
  font: ['.woff2', '.woff', '.ttf', '.otf'],
  image: ['.gif', '.jpg', '.jpeg', '.png', '.heic'],
  video: ['.mov', '.mp4', '.mpeg', '.webm', '.ogg'],
  audio: ['.mp3', '.wav', '.ogg'],
  document: ['.pdf', '.docx', '.txt', '.xlsx', '.pptx'],
  style: ['.css'],
  archive: ['.zip', '.rar', '.tar', '.gz'],
  code: ['.js', '.ts', '.jsx', '.tsx', '.html', '.css', '.scss', '.json'],
  other: ['.md', '.yaml', '.xml', '.csv', '.log']
};

function toggleStringInArray(arr: string[], str: string): string[] {
  const index = arr.indexOf(str);

  if (index === -1) {
    // If the string is not in the array, add it.
    arr.push(str);
  } else {
    // If the string is in the array, remove it.
    arr.splice(index, 1);
  }

  return arr;
}


const fileIcon = <svg width="100%" height="auto" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M0 64C0 28.7 28.7 0 64 0L224 0l0 128c0 17.7 14.3 32 32 32l128 0 0 288c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 64zm384 64l-128 0L256 0 384 128z"/></svg>;
const documentIcon = <svg width="100%" height="auto" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M64 0C28.7 0 0 28.7 0 64L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64L384 128l-128-128L64 0zm256 128l-96-96v96c0 17.7 14.3 32 32 32l64 0z"/></svg>;
const audioIcon = <svg width="100%" height="auto" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M256 32C141.1 32 48 125.1 48 240c0 114.9 93.1 208 208 208s208-93.1 208-208S370.9 32 256 32zm0 352c-79.5 0-144-64.5-144-144s64.5-144 144-144s144 64.5 144 144s-64.5 144-144 144z"/><path d="M256 128c-61.9 0-112 50.1-112 112s50.1 112 112 112s112-50.1 112-112s-50.1-112-112-112zm0 192c-44.2 0-80-35.8-80-80s35.8-80 80-80s80 35.8 80 80s-35.8 80-80 80z"/></svg>;
const videoIcon = <svg width="100%" height="auto" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path d="M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm0 48h448c8.8 0 16 7.2 16 16v256c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V96c0-8.8 7.2-16 16-16zM192 160v192l128-96L192 160z"/></svg>;
const folderIcon = <svg width="100%" height="auto" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M64 480H448c35.3 0 64-28.7 64-64V160c0-35.3-28.7-64-64-64H288c-10.1 0-19.6-4.7-25.6-12.8L243.2 57.6C231.1 41.5 212.1 32 192 32H64C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64z"/></svg>;
const ellipsisIcon = <svg width="100%" height="auto" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M8 256a56 56 0 1 1 112 0A56 56 0 1 1 8 256zm160 0a56 56 0 1 1 112 0 56 56 0 1 1 -112 0zm216-56a56 56 0 1 1 0 112 56 56 0 1 1 0-112z"/></svg>;
const checkIcon = <svg width="100%" height="auto" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/></svg>;
const exclamationIcon = <svg width="100%" height="auto" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M96 64c0-17.7-14.3-32-32-32S32 46.3 32 64l0 256c0 17.7 14.3 32 32 32s32-14.3 32-32L96 64zM64 480a40 40 0 1 0 0-80 40 40 0 1 0 0 80z"/></svg>;


const NewFolderModal: React.FC<{ 
  resolve: (value: { folderName: string } | null) => any, 
  existingFolderNames?: string[], 
}> = ({ resolve, existingFolderNames }) => {

  const [storageContext, setStorageContext] = React.useContext(StorageContext);

  const [model, setModel] = React.useState<{ folderName: string }>({ folderName: '' });
  const [errors, setErrors] = React.useState<{ key: string, message: string }[]>([]);
  const [touched, setTouched] = React.useState<boolean>(false);

  const formThemeClassList = (storageContext.theme as Theme)?.card1color || '';

  return (
    <div className='p-4'>
      <div className="text-center p-5">
        <label>Enter new folder name</label>
      </div>
      <QuickForm<{ folderName: string }> 
        labelPlacement={'none'}
        schema={{ 
          folderName: { 
            type: /^[a-zA-Z][a-zA-Z0-9-]*$/, 
            attributes: { 
              required: true,
              tests: [ (r,v,k) => (
                existingFolderNames?.map(fname => fname.toUpperCase()).includes(v.toUpperCase()) ?
                  { success: false, message: `Folder name already in use` }
                :
                  { success: true, message: 'Folder   name available.'}
              )]
            }, 
            meta: { quickForm: { inputClassName: `${formThemeClassList}`}} 
          }
        }}
        init={model}
        onInput={(e, m) => quickFormPartialInputHandler(model, m, e, setModel, setErrors, setTouched)}
        fieldIcons={{ valid: checkIcon, invalid: exclamationIcon }}
      />
      <button
        className={'btn btn-primary float-right mb-2'}
        disabled={!touched || !!errors.length}
        onClick={() => resolve({ folderName: model.folderName })}
      >
        Submit
      </button>
    </div>
  )
}

const FileBrowser: React.FC<FileBrowserProps> = ({ 
  onPick, 
  acceptedMediaExtensions = defaultAcceptedMediaExtensions,
  sources = {
    'Disc': {
      readDirectoryRoute: 'getdirectory',
      createDirectoryRoute: 'createdirectory',
      deleteMediaRoute: 'deletemedia',
      uploadMediaRoute: 'uploadmedia'
    }
  }
}) => {

  const modalContext = React.useContext(ModalContext);
  const [storageContext, setStorageContext] = React.useContext(StorageContext);
  
  const [source, setSource] = useState<keyof typeof sources>('Disc');
  const [currentDirectory, setCurrentDirectory] = useState<Directory>();
  const [directoryHistory, setDirectoryHistory] = useState<Directory[]>([]);
  const [path, setPath] = useState<string[]>(['root']);

  const [selected, setSelected] = useState<string[]>([]);

  const [search, setSearch] = useState<string>('');

  const [busy, setBusy] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const formThemeClassList = ''; //(storageContext.theme as Theme)?. || '';
  
  React.useEffect(() => { reload(); }, []);
  
  // Function to navigate into a subdirectory
  const navigateToSubDirectory = (subDirName: string) => {
    const subDirectory = currentDirectory?.subDirectories[subDirName];
    if (subDirectory) {
      setDirectoryHistory((prevHistory) => [...prevHistory, currentDirectory]);
      setCurrentDirectory(subDirectory);
      path.push(subDirName);
      setSelected([]);
    }
  };

  // Function to navigate back to the parent directory
  const navigateToParentDirectory = () => {
    const parentDirectory = directoryHistory.pop();
    if (parentDirectory) {
      setCurrentDirectory(parentDirectory);
      setDirectoryHistory(directoryHistory);
      path.pop();
      setSelected([]);
    }
  };

  const reload = async () => {
    setError(false);
    setBusy(true);
    const dirRes = await HttpService.get<Directory>(sources[source].readDirectoryRoute, { path: '/' });

    if (dirRes.success && dirRes.body) {
      const pathCopy = path.slice(1).reverse();
      let newDir = dirRes.body;
      let newHistory: Directory[] = []
      while (pathCopy.length) {
        newHistory.push(newDir);
        newDir = newDir.subDirectories[pathCopy.pop()!]
      }
      setDirectoryHistory(newHistory);
      setCurrentDirectory(newDir);
      setBusy(false);
    } else {
      setBusy(false);
      setError(true);
      console.log(dirRes);
    }
  }

  useEffect(() => { 
    setPath(['root']);
    setDirectoryHistory([]);
    setCurrentDirectory(undefined);
    reload(); 
  }, [source]);

  return (
    <div className="file-browser p-2 md:p-4 md:m-1">

      { source && Object.keys(sources).length > 1 &&

        <div className='flex items-center py-4'>
          <h2 className='text-md text-slate-600 mr-4'>Source</h2>
          <select className='select border-2 border-black' onChange={s => setSource(s.target.value as keyof typeof sources)} value={source}>
            { Object.keys(sources).map(src => (<option key={src} value={src}>{src}</option>)) }
          </select>
        </div>
      
      }

      <div className="flex items-center mx-2 mb-3">
        {/* Render ellipsis icon to go back to parent directory */}
        {directoryHistory.length > 0 && (
          <button onClick={navigateToParentDirectory} className={`btn mr-4 w-10 p-3`}>
            {ellipsisIcon}
          </button>
        )}
        <h2 className="text-sm text-slate-400">location:&nbsp;{`${path.join('\/')}`}</h2>
        <h2 className='text-slate-500 text-xs text-right grow p-0'>size:&nbsp;{((currentDirectory?.size || 0)/1024/1024).toFixed(2)}mb</h2>
      </div>

      <ColumnSnapper wideColumns={4} narrowColumns={2} minColumnPixWidth={400}>

        {/* delete files button */}
        <button 
          className={`btn btn-danger m-1 ml-2`}
          disabled={!selected.length || !sources[source].deleteMediaRoute}
          onClick={async () => {
            if (selected.length) {
              const modalRes = await modalContext.modal!({
                prompt: 'Are you sure you want to delete selected files?',
                options: ['yes', 'no']
              });
              if (modalRes === 'yes') {
                const delRes = await HttpService.delete(sources[source].deleteMediaRoute!, { filenames: JSON.stringify(selected.map(s => ([...path].slice(1) || []).join('/') + '/' + s)) })
                if (delRes.success) {
                  modalContext.toast!('success', delRes.messages[0]);
                  setSelected([]);
                  reload();
                } else {
                  modalContext.toast!('warning', delRes.messages[0]);
                  setSelected([]);
                }
              }
            }
          }}
        >Delete</button>

        {/* delete folder */}
        <button 
          className={`btn btn-primary m-1`}
          disabled={!sources[source].deleteMediaRoute}
          onClick={async () => {
            const modalRes = await modalContext.modal!({
              prompt: `Are you sure you want to delete current folder ${path.at(-1)}, its subdirectories, and files?`,
              options: ['yes', 'no']
            });
            if (modalRes === 'yes') {
              const delRes = await HttpService.delete(sources[source].deleteMediaRoute!, { filenames: JSON.stringify([ ([...path].slice(1) || []).join('/') ]) })
              if (delRes.success) {
                modalContext.toast!('success', delRes.messages[0]);
                setSelected([]);
                navigateToParentDirectory();
                reload();
              } else {
                modalContext.toast!('warning', delRes.messages[0]);
                setSelected([]);
              }
            }
          }}
        >Delete Folder</button>
        
        {/* new folder */}
        <button 
          className={`btn btn-secondary m-1`}
          disabled={!sources[source].createDirectoryRoute}
          onClick={async () => {
            const modalRes = await (new Promise<{ folderName: string} | null>((res, rej) => modalContext.modal!({
              node: (<NewFolderModal resolve={res} existingFolderNames={Object.keys(currentDirectory?.subDirectories || {})}/>),
              resolve: res , reject: rej
            })));
            modalContext.modal!();
            if (modalRes?.folderName?.length) {
              const cdRes = await HttpService.post(sources[source].createDirectoryRoute!, { folderName: '/' + [...path, modalRes.folderName].slice(1).join('/') });
              if (cdRes.success) {
                modalContext.toast!('success', cdRes.messages[0])
                reload();
              } else {
                modalContext.toast!('warning', cdRes.messages[0]);
                console.log(cdRes);
              }

            }
          }}
        >New Folder</button>

        {/* upload button */}
        <button
          disabled={!sources[source].uploadMediaRoute} 
          className={`btn btn-primary m-1 mr-2`}
          onClick={async () => {
            // Create a new file input element
            const fileInput = document.createElement('input');
            fileInput.multiple = true;
            fileInput.accept = Object.values(acceptedMediaExtensions).flatMap(ext => ext).join(',');
            fileInput.type = 'file';
            fileInput.click();
            fileInput.onchange = async (event: Event) => {
              const target = event.target as HTMLInputElement;
              const files = Array.from(target.files || []);
              
              if (files && files.length) {
                // console.log('File selected:', file);
                const uploadRes = await HttpService.upload<string[]>(sources[source].uploadMediaRoute!, files, path.length > 1 ? { path: ([...path].slice(1) || []).join('/') } : {} );
                if (uploadRes.success && uploadRes.body) { 
                  modalContext.toast!('success', uploadRes.messages[0])
                  reload();
                } else {
                  modalContext.toast!('warning', uploadRes.messages[0]);
                  console.log(uploadRes)
                }
              }
              document.body.removeChild(fileInput);
            };
            document.body.appendChild(fileInput);
          }}
        >Upload</button>

      </ColumnSnapper>

      {/* search bar */}
      <input 
        className={`input-primary w-[calc(100%-16px)] text-left bg-blue-500 text-gray-500 ${formThemeClassList} p-3 mx-2 my-3`} 
        placeholder='search'
        onChange={(e) => setSearch(e.target.value)}
      >
      </input>

      {/* Render subdirectories */}
      <div className='relative w-full md:mx-2 md:w-auto md:min-w-[32rem]'>
        <div className="absolute top-0 left-0 w-full h-full -z-0">
          { sources[source].uploadMediaRoute ?
              <DragNDropUploader
                hideInstructions={true}
                uploadRoute={sources[source].uploadMediaRoute!}
                path={([...path].slice(1) || []).join('/')}
                acceptedFileExtensions={Object.values(acceptedMediaExtensions).flatMap(ext => ext)}
                onUpload={async (res) => { 
                  const resVal = await res;
                  // console.log('upload path: ', ([...path].slice(1) || []).join('/'));
                  if (resVal.success) {
                    reload();
                  } else {
                    console.log(resVal);
                  }
                }}
              />
            :
              <div className="flex flex-col items-center justify-center w-full h-full border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"></div>
          }
        </div>
        <div className="relative top-0 left-0 w-full h-full min-h-[24rem] z-10 pointer-events-none">
          { !!busy && <div className='h-full min-h-[24rem] flex justify-center items-center text-teal-500'><Spinner/></div> }
          {
            !!error &&
              <div className="h-full min-h-[24rem] flex flex-col justify-center items-center text-red-400">
                <Spinner/>
                <p className="neon-text font-black  text-lg">Error..</p>
              </div>
          }
          { 
            Object.entries(currentDirectory?.subDirectories || {}).sort(([a], [b]) => a.localeCompare(b)).filter(([dname]) => dname.includes(search)).map(([subDirName, subDir]) => (
              <div 
                key={subDirName} 
                className="cursor-pointer m-1 inline-flex flex-col items-center space-x-2 mb-2 pointer-events-auto"
                onClick={() => navigateToSubDirectory(subDirName)}
              >
                <div className="w-12 p-1 text-orange-400">{folderIcon}</div>
                <div className='w-32 text-center'>
                  <div
                    
                    className="text-blue-500 hover:underline"
                  >
                    <p className='text-center max-w-[8rem] break-words'>{subDirName}</p>
                  </div>
                </div>
              </div>
            ))
          }
          {
            Object.entries(currentDirectory?.files || {})
              .filter(([fname]) => Object.values(acceptedMediaExtensions).flatMap(arr => arr).filter(ext => fname.toLowerCase().endsWith(ext)).length > 0)
              .filter(([dname]) => dname.includes(search)).sort(([a], [b]) => a.localeCompare(b))
              .map(([filename, file]) => (
              
              <div 
                key={filename} 
                className={`cursor-pointer m-1 inline-flex flex-col items-center space-x-2 mb-2 pointer-events-auto ${selected.indexOf(filename) > -1 ? "bg-blue-400 bg-opacity-75" : ""}`}
                onClick={() => {
                  setSelected(s => toggleStringInArray([...s], filename));
                  onPick?.(
                    file.googleId ?
                        `https://drive.google.com/file/d/${file.googleId}/view?usp=sharing`
                      :
                        config.ASSETS[config.ENVIRONMENT] + [...path, filename].slice(1).join('/'), //'/' + [...path, filename].slice(1).join('/')
                    { googleId: file.googleId, ...file.metaData }
                  );
                }}
              >
                {
                  ['image', 'video', 'audio'].includes(getMediaType(filename.toLowerCase())) ? (

                    <div className="w-12 p-1 text-indigo-400 h-12">
                      <Media 
                        src={
                        file.googleId ?
                          `https://drive.google.com/file/d/${file.googleId}/view?usp=sharing`
                        :
                          config.ASSETS[config.ENVIRONMENT] + [...path, filename].slice(1).join('/')
                        }
                        googleMedia={
                          (file.metaData && file.googleId && file.metaData.mimeType && file.metaData.width && file.metaData.height) ? {
                            id: file.googleId,
                            mimeType: file.metaData.mimeType,
                            width: 48,
                            height: 48
                          } : undefined
                        }
                        fallback={
                          <div className="w-10 p-1 text-indigo-400">{fileIcon}</div>
                        }
                      />
                    </div>
                  ) : (
                    <div className="w-10 p-1 text-indigo-400">{fileIcon}</div>
                  )
                }
                <div className='w-32'>
                  <p className='text-center max-w-[8rem] break-words'>{filename}</p>
                  <p className='text-center max-w-[8rem] break-words'>({(file.size/1024/1024).toFixed(2)}mb)</p>
                  <p className='text-center max-w-[8rem] break-words'>{getMediaType(filename)}</p>
                </div>
              </div>
            ))
          }
        </div>
      </div>


    </div>
  );
};

export default FileBrowser;
